<template>
    <div class="site">
        <div v-if="store.currentUser && store.currentUser.isImpersonating"
             class="relative impersonate-bar mdui-theme-dark flex flex-wrap gap-1 pt-3 lg:pt-1 px-4 py-1 justify-between items-center bg-background text-on-surface z-40">
            <p class="flex gap-2 items-center body-medium truncate">
                <span>{{ $t('admin.impersonate.$headerbarText') }}</span>
                <span>{{ store.currentUser.name }}</span>
            </p>
            <div class="flex gap-2 items-center justify-between basis-full lg:basis-auto border-t lg:border-t-0 border-surface-variant min-w-0">
                <p class="body-small block truncate">{{ store.currentUser.impersonatorName }}</p>
                <mdui-button variant="text" @click="stopImpersonation" class="mdui-theme-dark text-primary btn-clipped" v-if="store.currentUser">
                    <mdui-icon name="person_off--outlined" slot="icon" class="text-18"></mdui-icon>
                    {{ $t('admin.impersonate.$stop') }}
                </mdui-button>
            </div>
        </div>
        <div :class="[currentRouteIsLogin ? '' : store.railOpen ? 'lg:ps-[280px]' : 'lg:ps-[76px]']">
            <aside :class="[
                'overflow-hidden transition lg:transform-none fixed lg:relative shrink-0 w-[81%] lg:w-[280px] inset-y-0 left-0 z-30 bg-primary-30 text-white',
                { '-translate-x-full': !store.menuOpen }]">
                <navigation v-if="!currentRouteIsLogin" :impersonating="store.currentUser?.isImpersonating" :railOpen="store.railOpen"></navigation>
            </aside>
            <transition>
                <div class="bg-black/20 z-20 fixed inset-0 lg:hidden" @click="hidemenu" v-if="store.menuOpen"></div>
            </transition>
            <main :class="[ 'main-content flex flex-col w-full',
                      !currentRouteIsLogin ? 'has-rail' : '',
                      store.currentUser && store.currentUser.isImpersonating ? 'has-topbar' : '' ]">
                <pageheader v-if="!currentRouteIsLogin" :is-cart="currentRouteIsCart" />
                <router-view v-slot="{ Component }">
                    <transition mode="out-in">
                        <component :is="Component" />
                    </transition>
                </router-view>
            </main>
            <mdui-snackbar placement="top" ref="snackbar" closeable close-on-outside-click>
                <div class="flex gap-4 lg:gap-5 items-center">
                    <mdui-icon v-if="notificationIcon" :name="notificationIcon" class="pb-1 text-tertiary-fixed text-20 text-32"></mdui-icon>
                    <span v-html="notification"></span>
                </div>
            </mdui-snackbar>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, onMounted, useTemplateRef, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useRoute, useRouter, RouterView } from 'vue-router';
    import { useStore } from '@/store';
    import { useAuth } from './composables/auth';
    import emitter, { Events } from '@/event';
    import BasketService from './services/basketService';
    import InfoService from '@/services/infoService';
    import Navigation from '@/partials/Navigation.vue';
    import Pageheader from '@/partials/Pageheader.vue';
    import 'mdui/components/button.js';
    import 'mdui/components/snackbar.js';
    import { useBasket } from './composables/basket';
    import type { Snackbar } from 'mdui/components/snackbar.js';
    import ImpersonationService from '@/services/impersonationService';
    import { getFirstAccessibleRoute, isRoutePermited } from '@/router';

    // Usings
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { logout } = useAuth();
    const { getBasket } = useBasket();
    const { t } = useI18n();

    // Services
    const infoService = new InfoService();
    const basketService = new BasketService();
    const impersonationService = new ImpersonationService();

    // Template
    const snackbar = useTemplateRef<Snackbar>('snackbar');

    // Data
    const notification = ref<string>();
    const notificationIcon = ref<string>();

    // Watch for debitor changes to enforce permissions immediately
    watch(() => store.currentDebitor, (newDebitor) => {
        if (!newDebitor) return;

        const perms = newDebitor.debitorPermissions;
        if (!isRoutePermited(route, perms)) {
            const destination = getFirstAccessibleRoute(perms);
            router.push(destination);
        }
    }, { deep: true });

    // Lifecycle
    onMounted(() => {
        // If we have a debitor selected on load, fetch the basket immediately
        if (store.currentDebitor) {
            getBasket();
        }
    });

    // Events
    emitter.on(Events.ShowMenu, () => (store.menuOpen = true));
    emitter.on(Events.HideMenu, () => (store.menuOpen = false));
    emitter.on(Events.ShowRail, () => (store.railOpen = true));
    emitter.on(Events.HideRail, () => (store.railOpen = false));
    emitter.on(Events.AddNotification, (payload) => {
        const { message, icon } = typeof payload === 'string' ? { message: payload, icon: undefined } : payload as any;
        openNotification(message, false, icon);
    });
    emitter.on(Events.AddStaticNotification, (payload) => {
        const { message, icon } = typeof payload === 'string' ? { message: payload, icon: undefined } : payload as any;
        openNotification(message, true, icon);
    });
    emitter.on(Events.LoggedOut, () => logout());
    emitter.on(Events.UpdateBasket, () => getBasket());

    // Computed
    const currentRouteIsLogin = computed(() => route.path.includes('login'));
    const currentRouteIsCart = computed(() => route.path.includes('cart'));

    // Methods
    async function stopImpersonation() {
        try {
            await impersonationService.StopImpersonation();
            await infoService.GetMemberInfo().then((response) => {
                if (response.status == 200) {
                    store.currentUser = response.data;
                    store.currentDebitor = undefined;
                    router.push({ name: 'admin' });
                }
            });
        } catch (e) {
            logout()
            emitter.emit(Events.AddNotification, t('admin.impersonate.error.$stopImpersonationFailed'));
        }
    }

    function hidemenu() {
        emitter.emit(Events.HideMenu);
    }
    function hiderail() {
        emitter.emit(Events.HideRail);
    }

    function openNotification(message: string, keepOpen: boolean, icon?: string) {
        if (!snackbar.value) return;
        notification.value = message;
        notificationIcon.value = icon;
        snackbar.value.autoCloseDelay = keepOpen ? 0 : 7000;
        snackbar.value.open = true;
    }

</script>

<style>
    /* Transitions works when added to the global style */
    .v-enter-active,
    .v-leave-active {
        transition-property: opacity;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
    }

    .v-enter-from,
    .v-leave-to {
        opacity: 0;
    }

    .fade-up-enter-active,
    .fade-up-leave-active {
        transition-property: opacity, transform;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
    }

    .fade-up-enter-from,
    .fade-up-leave-to {
        opacity: 0;
        transform: translateY(16px);
    }
</style>
